<template>
  <label class="input-radio-big" :class="{ '-selected': selected, '-disabled': disabled }">
    <input
      type="radio"
      v-model="radioButtonValue"
      :value="data"
      :disabled="disabled"
    />
    <lucide-icon :name="icon" class="input-radio-big__icon" />
    <h3 class="input-radio-big__label">
      <slot></slot>
    </h3>
    <div v-if="$slots.details" class="input-radio-big__details">
      <slot name="details"></slot>
    </div>

    <svg
      class="input-radio-big__graphic"
      height="28"
      width="28"
      viewBox="0 0 28 28"
    >
      <circle
        r="13"
        cx="14"
        cy="14"
        class="input-radio-big__circle"
        :class="{ 'is-on': selected }"
      />
      <circle
        r="9"
        :style="{
          transform: `scale3d(${selected ? 1 : 0}, ${selected ? 1 : 0}, 1)`
        }"
        cx="14"
        cy="14"
        class="input-radio-big__mark"
      />
    </svg>
  </label>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LucideIcon from '@core/ui/LucideIcon.vue';
import type { IconName } from '../ui/lucideIcons';
const props = defineProps<{
  value: any;
  data: any;
  disabled: boolean;
  icon: IconName;
}>();
const emit = defineEmits<{
  (event: 'update:value', value: any): void;
}>();

const selected = computed(() => props.value === props.data);

const radioButtonValue = computed({
  get() {
    return props.value;
  },
  set() {
    emit("update:value", props.data);
  }
});
</script>

<style lang="scss">
.input-radio-big {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  border-radius: 0.5rem;
  gap: 0.75rem;
  border: 1px solid var(--color-border);
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;

  &.-selected {
    border-color: var(--color-primary);
    // background-color: var(--color-background-medium);
    outline: 2px solid var(--color-primary);
  }
  &:has(input:focus-visible) {
    outline: 3px dashed var(--color-primary);
    outline-offset: 4px;
  }

  &:hover {
    border-color: var(--color-primary);
  }
  &.-disabled {
    color: var(--color-text-light);
    border-color: var(--color-border);
    outline-color: var(--color-border);
    cursor: default;
  }

  input {
    position: absolute;
    opacity: 0;
    left: 0;
    top: 0;
  }

  &__label {
    margin: 0;
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
    font-weight: 500;
  }
  &.-selected &__label {
    color: var(--color-primary);
  }
  &.-disabled &__label {
    color: var(--color-text-light);
  }

  &__details {
    font-size: 1rem;
    line-height: 1.3;
    color: var(--color-gray-500);
    margin-bottom: 1rem;
  }

  &__icon {
    font-size: 2.5rem;
  }
  &.-selected &__icon {
    color: var(--color-primary);
  }
  &.-disabled &__icon {
    color: var(--color-text-light);
  }

  &__graphic {
    margin-top: auto;
    display: block;
    flex-shrink: 0;
    height: 2rem;
    width: 2rem;
    aspect-ratio: 1 / 1;
    border-radius: 9000px;
    overflow: hidden;
    contain: strict;
  }

  &__input:focus-visible ~ &__graphic {
    outline: 2px dotted var(--color-action);
    outline-offset: 1px;
  }

  &__circle {
    fill: transparent;
    stroke: var(--color-border);
    stroke-width: 2px;
    transition: fill 125ms;
    &.is-on {
      stroke: var(--color-action);
    }
  }
  &.-disabled &__circle {
    stroke: var(--color-text-light);
  }

  &__mark {
    fill: var(--color-action);
    stroke: none;
    transition: transform 100ms;
    transform-origin: 50% 50%;
  }
  &.-disabled &__mark {
    fill: var(--color-text-light);
  }
}
</style>